import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, FileText, AlertCircle, Loader, CheckCircle, XCircle, ZoomIn, ZoomOut, RefreshCw } from 'lucide-react';
import DatePickerField from './UI/DatePickerField';
import toast from '../utils/toast';
import { confidentialAPI, staffAPI } from '../services/api';
import api from '../services/api';
import Portal from './UI/Portal';
import ConfirmModal from './UI/ConfirmModal';
import { InfoBadge } from './UI/InfoBadge';
import { StatusBadge } from './UI/StatusBadge';
import { useIsMobile } from '../hooks/useIsMobile';
import { useImageViewer } from '../hooks/useImageViewer';
import MobileVerificationTabs from './UI/MobileVerificationTabs';
import VerificationPanelHeader from './UI/VerificationPanelHeader';
import ExtractAgainButton from './UI/ExtractAgainButton';
import PdfViewer from './UI/PdfViewer';
import FormSelect from './UI/FormSelect';
import LeaveHalfDaySegmentedControl from './Leave/LeaveHalfDaySegmentedControl';
import { toLocalISODate, parseLocalISOToDate, formatDateTime } from '../utils/dateUtils';
import { pluralizeDays } from '../utils/numberUtils';
import { isHeicFile, convertHeicBlobToJpeg, getPreviewDocumentType, inferContentType } from '../utils/imageUtils';

const NSDocumentVerificationModal = ({
  documentApprovalId,
  leaveApplicationId,
  applicationId,
  isOpen,
  onClose,
  onVerificationComplete,
  employeeName: employeeNameProp = null,
  employeeStaffCode: employeeStaffCodeProp = null,
}) => {
  // Loading state
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [rerunningOcr, setRerunningOcr] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);

  // Tracks whether the verification was finalised (approved or rejected).
  // When true, closing the modal skips the confirmation dialog.
  const wasFinalisedRef = useRef(false);
  const initialFormDataRef = useRef(null);
  
  // Document and leave application data
  const [verificationData, setVerificationData] = useState(null);
  const [error, setError] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    serial_number: '',
    issue_date: '',
    activity_type: '',
    leave_start_date: '',
    leave_end_date: '',
    leave_start_time_period: 'AM',
    leave_end_time_period: 'PM',
    notes: ''
  });
  
  // Leave days calculation
  const [calculatedLeaveDays, setCalculatedLeaveDays] = useState(0);
  const [calcLoading, setCalcLoading] = useState(false);
  const calculationAbortControllerRef = useRef(null);
  
  // Document preview state
  const [documentBlobUrl, setDocumentBlobUrl] = useState(null);
  const [loadingDocument, setLoadingDocument] = useState(false);
  const [documentError, setDocumentError] = useState(null);
  const documentBlobUrlRef = useRef(null);
  const documentAbortControllerRef = useRef(null);
  
  const viewer = useImageViewer();
  
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState('document');
  
  // NS Activity Types
  const nsActivityTypes = [
    'ICT (In-Camp Training)',
    'Training / Exercise (non-ICT)',
    'Meeting / Briefing',
    'Medical (screening / appointment)',
    'Fitness (IPPT / NS FIT / RT)',
    'Others'
  ];
  
  // Map OCR-extracted activity values to canonical dropdown values
  const mapOcrActivityType = (ocrValue) => {
    if (!ocrValue) return '';
    // Direct match first
    if (nsActivityTypes.includes(ocrValue)) return ocrValue;
    // Known OCR variations mapped to new canonical values
    const lower = ocrValue.toLowerCase();
    const mapping = {
      'ict': 'ICT (In-Camp Training)',
      'in-camp training (ict)': 'ICT (In-Camp Training)',
      'in-camp training': 'ICT (In-Camp Training)',
      'in-camp training (reservist)': 'ICT (In-Camp Training)',
      'reservist': 'ICT (In-Camp Training)',
      'make-up training': 'Training / Exercise (non-ICT)',
      'make-up training (mut)': 'Training / Exercise (non-ICT)',
      'course': 'Training / Exercise (non-ICT)',
      'mobilisation': 'Training / Exercise (non-ICT)',
      'orns': 'Training / Exercise (non-ICT)',
      'operationally ready national service (orns)': 'Training / Exercise (non-ICT)',
      'briefing / seminar / meeting': 'Meeting / Briefing',
      'meeting': 'Meeting / Briefing',
      'briefing': 'Meeting / Briefing',
      'medical review / medical': 'Medical (screening / appointment)',
      'medical review': 'Medical (screening / appointment)',
      'medical': 'Medical (screening / appointment)',
      'ippt': 'Fitness (IPPT / NS FIT / RT)',
      'ippt / ns fit': 'Fitness (IPPT / NS FIT / RT)',
      'ippt/ns fit': 'Fitness (IPPT / NS FIT / RT)',
      'ns fit': 'Fitness (IPPT / NS FIT / RT)',
      'individual physical proficiency test (ippt)': 'Fitness (IPPT / NS FIT / RT)',
    };
    if (mapping[lower]) return mapping[lower];
    // Fuzzy match — find dropdown option containing the OCR value
    const fuzzy = nsActivityTypes.find(t => t.toLowerCase().includes(lower));
    if (fuzzy) return fuzzy;
    // Fallback — default to Others
    return 'Others';
  };
  
  // Rejection mode state
  const [rejectionMode, setRejectionMode] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');

  // Load verification data on mount
  useEffect(() => {
    if (isOpen && (documentApprovalId || applicationId || leaveApplicationId)) {
      loadVerificationData();
    } else {
      // Reset state when modal closes
      setVerificationData(null);
      setFormData({
        serial_number: '',
        issue_date: '',
        activity_type: '',
        leave_start_date: '',
        leave_end_date: '',
        leave_start_time_period: 'AM',
        leave_end_time_period: 'PM',
        notes: ''
      });
      initialFormDataRef.current = null;
      setError(null);
      setDocumentBlobUrl(null);
      setDocumentError(null);
      viewer.reset();
      setRejectionMode(false);
      setRejectionReason('');
    }
  }, [isOpen, documentApprovalId, applicationId, leaveApplicationId]);
  
  // Load document blob when file path is available
  useEffect(() => {
    if (verificationData?.document_approval?.file_path) {
      loadDocumentBlob(verificationData.document_approval.file_path);
    }
    
    // Cleanup blob URL on unmount
    return () => {
      if (documentBlobUrlRef.current) {
        URL.revokeObjectURL(documentBlobUrlRef.current);
        documentBlobUrlRef.current = null;
      }
      if (documentAbortControllerRef.current) {
        documentAbortControllerRef.current.abort();
      }
    };
  }, [verificationData?.document_approval?.file_path]);
  
  // Calculate leave days when dates change
  useEffect(() => {
    if (calculationAbortControllerRef.current) {
      calculationAbortControllerRef.current.abort();
    }
    
    if (!formData.leave_start_date || !formData.leave_end_date) {
      setCalculatedLeaveDays(0);
      setCalcLoading(false);
      return;
    }
    
    const timeoutId = setTimeout(async () => {
      calculationAbortControllerRef.current = new AbortController();
      setCalcLoading(true);
      
      try {
        const payload = {
          start_date: formData.leave_start_date,
          end_date: formData.leave_end_date,
          leave_type_name: 'National Service Leave',
          staff_id: verificationData?.document_approval?.employee_pk || undefined
        };
        const resp = await staffAPI.calculateLeaveDays(payload);
        let days = resp?.data?.calculation?.mom_compliant_days ?? 0;
        
        // Apply AM/PM adjustments
        if (formData.leave_start_time_period === 'PM') days -= 0.5;
        if (formData.leave_end_time_period === 'AM') days -= 0.5;
        
        setCalculatedLeaveDays(Math.max(0, Number(days)));
      } catch (error) {
        if (error.name === 'AbortError') {
          return;
        }
        console.error('Error calculating leave days:', error);
        setCalculatedLeaveDays(0);
      } finally {
        setCalcLoading(false);
      }
    }, 500);
    
    return () => {
      clearTimeout(timeoutId);
      if (calculationAbortControllerRef.current) {
        calculationAbortControllerRef.current.abort();
      }
    };
  }, [formData.leave_start_date, formData.leave_end_date, formData.leave_start_time_period, formData.leave_end_time_period]);
  
  const handleExtractAgain = async () => {
    const id = verificationData?.document_approval?.id || documentApprovalId;
    if (!id || rerunningOcr) return;
    setRerunningOcr(true);
    const loadingToast = toast.loading('Extracting details…');
    try {
      const formData = new FormData();
      formData.append('document_approval_id', id);
      const resp = await api.post('/ns-call-up/trigger-ocr/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      toast.dismiss(loadingToast);
      const payload = resp?.data || {};
      if (!payload.success) {
        toast.warning(
          payload.message || payload.error || 'Could not read this call-up. Fill in the fields from the document.',
          { duration: 6000 },
        );
        return;
      }
      const fresh = await confidentialAPI.getNSVerificationData(id);
      const ocr = fresh.data?.ocr_data || {};
      setVerificationData(fresh.data);
      setFormData((prev) => ({
        ...prev,
        serial_number: ocr.serial_number || prev.serial_number,
        issue_date: ocr.issue_date || prev.issue_date,
      }));
      if (ocr.serial_number || ocr.issue_date) {
        toast.success('Extracted details updated');
      } else {
        toast.warning(
          'Could not read a serial number from this call-up. Fill it in from the document.',
          { duration: 6000 },
        );
      }
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error(
        error?.response?.data?.message
        || error?.response?.data?.error
        || 'Failed to analyse document. Please try again.',
      );
    } finally {
      setRerunningOcr(false);
    }
  };

  // Load verification data
  const loadVerificationData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Use applicationId (leave app ID) when available — mirrors MC pattern.
      // Falls back to documentApprovalId (used by Dashboard which already has it).
      // Legacy leaveApplicationId prop also supported.
      const leaveId = applicationId || leaveApplicationId;
      let response;
      if (documentApprovalId) {
        response = await confidentialAPI.getNSVerificationData(documentApprovalId);
      } else if (leaveId) {
        response = await confidentialAPI.getNSVerificationDataByLeave(leaveId);
      } else {
        setError('No document or application ID provided.');
        setLoading(false);
        return;
      }
      const data = response.data;
      
      setVerificationData(data);
      
      // Populate form: merge document_approval → OCR → leave_application
      // Priority: leave_application fields win for dates/activity, but OCR fills
      // serial_number & issue_date when the DocumentApproval doesn't have them yet.
      const docApproval = data.document_approval || {};
      const ocr = data.ocr_data || {};
      const leaveApp = data.leave_application;

      const mergedSerial = docApproval.serial_number || ocr.serial_number || '';
      const mergedIssueDate = docApproval.issue_date || ocr.issue_date || '';

      const defaults = {
        serial_number: '',
        issue_date: '',
        activity_type: '',
        leave_start_date: '',
        leave_end_date: '',
        leave_start_time_period: 'AM',
        leave_end_time_period: 'PM',
        notes: ''
      };

      let populated;
      if (leaveApp) {
        populated = {
          ...defaults,
          serial_number: mergedSerial,
          issue_date: mergedIssueDate,
          activity_type: leaveApp.activity_type || mapOcrActivityType(ocr.activity_type) || '',
          leave_start_date: leaveApp.leave_start_date || ocr.leave_start_date || '',
          leave_end_date: leaveApp.leave_end_date || ocr.leave_end_date || '',
          leave_start_time_period: leaveApp.leave_start_time_period || ocr.leave_start_time_period || 'AM',
          leave_end_time_period: leaveApp.leave_end_time_period || ocr.leave_end_time_period || 'PM'
        };
        setCalculatedLeaveDays(leaveApp.leave_total_days || 0);
      } else if (ocr.serial_number || ocr.issue_date || ocr.leave_start_date) {
        populated = {
          ...defaults,
          serial_number: mergedSerial,
          issue_date: mergedIssueDate,
          activity_type: mapOcrActivityType(ocr.activity_type),
          leave_start_date: ocr.leave_start_date || '',
          leave_end_date: ocr.leave_end_date || '',
          leave_start_time_period: ocr.leave_start_time_period || 'AM',
          leave_end_time_period: ocr.leave_end_time_period || 'PM'
        };
      } else {
        populated = {
          ...defaults,
          serial_number: mergedSerial,
          issue_date: mergedIssueDate,
        };
      }

      setFormData(populated);
      initialFormDataRef.current = populated;
    } catch (error) {
      console.error('Error loading verification data:', error);
      setError(error.response?.data?.error || 'Failed to load verification data');
      toast.error('Failed to load verification data');
    } finally {
      setLoading(false);
    }
  };
  
  // Load document blob URL (aligned with MC pattern)
  const loadDocumentBlob = async (filePath) => {
    if (documentAbortControllerRef.current) {
      documentAbortControllerRef.current.abort();
    }
    const abortController = new AbortController();
    documentAbortControllerRef.current = abortController;
    
    try {
      setLoadingDocument(true);
      setDocumentError(null);
      
      if (documentBlobUrlRef.current) {
        URL.revokeObjectURL(documentBlobUrlRef.current);
        documentBlobUrlRef.current = null;
      }
      
      if (!filePath) {
        throw new Error('Missing document file path');
      }
      
      let apiPath = filePath;
      if (filePath.includes('/view/')) {
        const urlParts = filePath.split('/view/');
        apiPath = urlParts[urlParts.length - 1];
      } else if (filePath.startsWith('/documents/view/')) {
        apiPath = filePath.replace('/documents/view/', '');
      }
      
      const response = await api.get(`/documents/view/${encodeURIComponent(apiPath)}/`, {
        responseType: 'blob',
        signal: abortController.signal
      });
      
      if (abortController.signal.aborted) return;
      
      const fileExt = (apiPath.split('.').pop() || '').toLowerCase();
      const inferredType = inferContentType(fileExt, response.headers?.['content-type']);
      let blob = new Blob([response.data], { type: inferredType });

      if (isHeicFile(apiPath)) {
        blob = await convertHeicBlobToJpeg(blob);
      }

      const blobUrl = URL.createObjectURL(blob);
      
      if (documentBlobUrlRef.current) URL.revokeObjectURL(documentBlobUrlRef.current);
      documentBlobUrlRef.current = blobUrl;
      setDocumentBlobUrl(blobUrl);
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('Error loading document:', error);
      setDocumentError('Failed to load document preview');
    } finally {
      setLoadingDocument(false);
    }
  };

  // Retry loading document
  const retryLoadDocument = useCallback(() => {
    if (verificationData?.document_approval?.file_path) {
      setDocumentError(null);
      documentAbortControllerRef.current = new AbortController();
      loadDocumentBlob(verificationData.document_approval.file_path);
    }
  }, [verificationData?.document_approval?.file_path]);
  
  // Handle approve
  const handleApprove = async () => {
    if (!verificationData) return;
    
    setSubmitting(true);
    
    try {
      const payload = {
        document_approval_id: verificationData.document_approval?.id ?? documentApprovalId,
        action: 'approve',
        serial_number: formData.serial_number,
        issue_date: formData.issue_date,
        activity_type: formData.activity_type,
        leave_start_date: formData.leave_start_date,
        leave_end_date: formData.leave_end_date,
        leave_start_time_period: formData.leave_start_time_period,
        leave_end_time_period: formData.leave_end_time_period,
        notes: formData.notes
      };
      
      await confidentialAPI.verifyNSCallUp(payload);
      
      toast.success('NS Call-up Order and leave application approved successfully');
      
      if (onVerificationComplete) {
        onVerificationComplete();
      }
      
      wasFinalisedRef.current = true;
      onClose();
    } catch (error) {
      console.error('Error approving NS document:', error);
      toast.error(error.response?.data?.error || 'Failed to approve document');
    } finally {
      setSubmitting(false);
    }
  };
  
  // Handle reject — submit the rejection after reason is provided
  const handleRejectSubmit = async () => {
    if (!verificationData) return;
    
    if (!rejectionReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    
    setSubmitting(true);
    
    try {
      const payload = {
        document_approval_id: verificationData.document_approval?.id ?? documentApprovalId,
        action: 'reject',
        rejection_reason: rejectionReason.trim(),
        notes: formData.notes
      };
      
      await confidentialAPI.verifyNSCallUp(payload);
      
      toast.success('NS Call-up Order rejected');
      setRejectionMode(false);
      setRejectionReason('');
      
      if (onVerificationComplete) {
        onVerificationComplete();
      }
      
      wasFinalisedRef.current = true;
      onClose();
    } catch (error) {
      console.error('Error rejecting NS document:', error);
      toast.error(error.response?.data?.error || 'Failed to reject document');
    } finally {
      setSubmitting(false);
    }
  };
  
  // ── Close guard ────────────────────────────────────────────────────────────
  const nsDocStatus = (verificationData?.document_approval?.status || '').toLowerCase();
  const isAlreadyFinalised = ['approved', 'verified', 'rejected'].includes(nsDocStatus);

  const handleCloseRequest = useCallback(() => {
    if (wasFinalisedRef.current || isAlreadyFinalised) {
      onClose();
      return;
    }

    const hasFormChanges = initialFormDataRef.current &&
      JSON.stringify(formData) !== JSON.stringify(initialFormDataRef.current);
    const hasRejectionDraft = rejectionMode && rejectionReason.trim();

    if (hasFormChanges || hasRejectionDraft) {
      setShowCloseConfirm(true);
      return;
    }

    onClose();
  }, [onClose, isAlreadyFinalised, formData, rejectionMode, rejectionReason]);

  // Determine document type for preview
  const getDocumentType = () => getPreviewDocumentType(verificationData?.document_approval?.file_path);
  
  if (!isOpen) return null;
  
  return (
    <>
    <Portal>
      <div className="modal-overlay">
        <div className={`modal-container modal-container-xl ${isMobile ? 'h-[calc(100dvh-1rem)]' : 'max-h-[90vh]'}`}>
          {/* Modal Header — DS: title + InfoBadge; HR context subtitle; close alone */}
          <div className="modal-header">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 min-w-0 flex-wrap">
                <h3 className="modal-title truncate min-w-0">NS Verification</h3>
                <InfoBadge color="green" label="National Service Leave" className="flex-shrink-0" />
              </div>
              {(() => {
                const name = employeeNameProp
                  || verificationData?.leave_application?.staff_name
                  || verificationData?.document_approval?.employee_name;
                const code = employeeStaffCodeProp
                  || verificationData?.leave_application?.staff_id
                  || verificationData?.document_approval?.employee_staff_id;
                if (!name && !code) return null;
                return (
                  <p className="text-sm text-gray-500 mt-0.5 truncate">
                    {name || ''}
                    {code && (
                      <>
                        <span className="mx-1.5 text-gray-400">·</span>
                        <span>{code}</span>
                      </>
                    )}
                  </p>
                );
              })()}
            </div>
            <button
              type="button"
              onClick={handleCloseRequest}
              className="modal-close-admin flex-shrink-0"
              aria-label="Close modal"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {/* Modal Content - override overflow-y-auto so each panel scrolls independently */}
          <div className="modal-content !overflow-hidden !p-0 flex flex-col">
            {loading ? (
              <div className="flex-1 flex items-center justify-center p-8">
                <div className="text-center">
                  <Loader className="w-12 h-12 mx-auto text-red-600 animate-spin mb-4" />
                  <p className="text-sm text-gray-600">Loading verification data...</p>
                </div>
              </div>
            ) : error ? (
              <div className="flex-1 flex items-center justify-center p-8">
                <div className="text-center max-w-md">
                  <AlertCircle className="w-12 h-12 mx-auto text-red-500 mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Loading Data</h3>
                  <p className="text-sm text-gray-600 mb-6">{error}</p>
                  <button
                    onClick={loadVerificationData}
                    className="btn btn-admin-primary"
                  >
                    Retry
                  </button>
                </div>
              </div>
            ) : verificationData ? (
              <div className={`flex-1 flex ${isMobile ? 'flex-col' : 'flex-row'} overflow-hidden min-h-0`}>
                {/* Mobile tab bar */}
                {isMobile && (
                  <MobileVerificationTabs
                    activeTab={activeTab}
                    onTabChange={setActiveTab}
                    documentLabel="Document"
                    formLabel="Leave Details"
                    isAdmin={true}
                  />
                )}
                {/* Left Panel - Document Preview */}
                <div className={`${isMobile ? (activeTab === 'document' ? 'flex-1' : 'hidden') : 'w-2/3'} ${!isMobile ? 'border-r' : ''} border-gray-200 flex flex-col bg-gray-50 min-h-0`}>
                  {/* Desktop only — mobile tabs already label the Document surface */}
                  {!isMobile && (
                    <VerificationPanelHeader title="NS Call-up Order" />
                  )}
                  <div className="flex-1 p-4 overflow-hidden">
                    <div className="relative w-full h-full" style={isMobile ? undefined : { minHeight: '600px' }}>
                      {loadingDocument ? (
                        <div className="flex items-center justify-center h-full text-gray-500">
                          <div className="text-center">
                            <div className="w-8 h-8 border-4 border-red-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                            <p>Loading document...</p>
                          </div>
                        </div>
                      ) : documentBlobUrl && getDocumentType() === 'image' ? (
                        <div
                          ref={viewer.containerRef}
                          className="relative w-full h-full overflow-hidden bg-white border rounded-lg flex items-center justify-center"
                          {...viewer.panHandlers}
                          style={viewer.containerStyle}
                        >
                          <img
                            src={documentBlobUrl}
                            alt="NS Call-up Order"
                            className="select-none pointer-events-none"
                            draggable={false}
                            style={{
                              transform: viewer.imageTransform,
                              transformOrigin: 'center center',
                              maxWidth: '100%',
                              maxHeight: '100%',
                              transition: viewer.isPanning ? 'none' : 'transform 0.15s ease',
                            }}
                          />
                          <div
                            className="absolute bottom-3 right-3 z-10 flex items-center gap-0.5 bg-white/95 border border-gray-200 rounded-full shadow-md p-1"
                            onMouseDown={e => e.stopPropagation()}
                          >
                            <button
                              type="button"
                              onClick={viewer.zoomOut}
                              className="icon-button-admin"
                              title="Zoom out"
                              aria-label="Zoom out"
                            >
                              <ZoomOut className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              onClick={viewer.zoomIn}
                              className="icon-button-admin"
                              title="Zoom in"
                              aria-label="Zoom in"
                            >
                              <ZoomIn className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              onClick={viewer.reset}
                              className="icon-button-admin"
                              title="Fit to panel"
                              aria-label="Fit to panel"
                            >
                              <RefreshCw className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ) : documentBlobUrl && getDocumentType() === 'pdf' ? (
                        <div className="w-full h-full border rounded-lg bg-white overflow-hidden" style={isMobile ? undefined : { minHeight: '600px' }}>
                          <PdfViewer
                            src={documentBlobUrl}
                            title="NS Call-up Order"
                            fileName={`NS_${employeeStaffCodeProp || 'document'}.pdf`}
                          />
                        </div>
                      ) : documentError ? (
                        <div className="flex items-center justify-center h-full text-gray-500">
                          <div className="text-center">
                            <AlertCircle className="w-12 h-12 mx-auto mb-4 text-red-500" />
                            <p className="text-red-600 font-medium mb-2">Failed to load document</p>
                            <p className="text-sm text-gray-500 mb-4">{documentError}</p>
                            <button
                              onClick={retryLoadDocument}
                              className="btn btn-sm btn-admin-outline"
                            >
                              Retry Loading
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center h-full text-gray-500">
                          <div className="text-center">
                            <FileText className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                            <p>Document preview not available</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Right Panel - Form Fields */}
                <div className={`${isMobile ? (activeTab === 'form' ? 'flex-1' : 'hidden') : 'w-1/3'} flex flex-col min-h-0 min-w-0 overflow-hidden`}>
                  {/* Desktop only — mobile tabs already label Leave Details */}
                  {!isMobile && (
                    <VerificationPanelHeader
                      title="Leave Details"
                      trailing={['submitted', 'pending', 'ocr_completed', 'processing', 'processing_ocr'].includes(
                        (verificationData?.document_approval?.status || '').toLowerCase(),
                      ) ? (
                        <ExtractAgainButton onClick={handleExtractAgain} busy={rerunningOcr} />
                      ) : null}
                    />
                  )}
                  <div className="flex-1 overflow-y-auto overflow-x-hidden overscroll-y-contain p-4 space-y-4" style={{ touchAction: 'pan-y', WebkitOverflowScrolling: 'touch' }}>
                    {isMobile && ['submitted', 'pending', 'ocr_completed', 'processing', 'processing_ocr'].includes(
                      (verificationData?.document_approval?.status || '').toLowerCase(),
                    ) ? (
                      <div className="flex justify-end">
                        <ExtractAgainButton onClick={handleExtractAgain} busy={rerunningOcr} />
                      </div>
                    ) : null}
                    {/* Employee Info */}
                    {(verificationData.leave_application || verificationData.document_approval) && (
                      <div className="bg-gray-50 rounded-lg p-3">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {verificationData.leave_application?.staff_name || verificationData.document_approval?.employee_name || 'N/A'}
                            </p>
                            <p className="text-xs text-gray-600">
                              {verificationData.leave_application?.staff_id || verificationData.document_approval?.employee_staff_id || 'N/A'}
                            </p>
                          </div>
                          {verificationData.leave_application && (
                            <StatusBadge
                              status={verificationData.leave_application.ro_acknowledged_at ? 'acknowledged' : 'pending'}
                              label={verificationData.leave_application.ro_acknowledged_at ? 'RO Acknowledged' : 'Awaiting RO'}
                            />
                          )}
                        </div>
                        {verificationData.leave_application && (
                          <p className="mt-2 pt-2 border-t border-gray-200 text-[11px] leading-4 text-gray-500">
                            {verificationData.leave_application.ro_acknowledged_at
                              ? `By ${
                                  [
                                    verificationData.leave_application.ro_acknowledged_by_name,
                                    verificationData.leave_application.ro_acknowledged_by_staff_id,
                                  ].filter(Boolean).join(' · ') || 'reporting officer'
                                } on ${formatDateTime(verificationData.leave_application.ro_acknowledged_at)}`
                              : 'RO acknowledgement is pending; HR verification can continue.'}
                          </p>
                        )}
                      </div>
                    )}
                    
                    {/* Total Days Display */}
                    <div className="flex items-center justify-between bg-blue-50 rounded-lg p-3">
                      <span className="text-sm font-medium text-gray-700">Total Days</span>
                      {calcLoading ? (
                        <div className="inline-flex items-center">
                          <Loader className="w-4 h-4 text-blue-600 animate-spin mr-2" />
                          <span className="text-sm text-gray-600">Calculating...</span>
                        </div>
                      ) : (
                        <span className="text-base font-semibold text-gray-900">
                          {calculatedLeaveDays} {pluralizeDays(calculatedLeaveDays)}
                        </span>
                      )}
                    </div>
                    
                    {/* Serial Number */}
                    <div className="form-group">
                      <label className="form-label">
                        Call-up Serial Number
                      </label>
                      <input
                        type="text"
                        value={formData.serial_number}
                        onChange={(e) => setFormData(prev => ({ ...prev, serial_number: e.target.value }))}
                        placeholder="Enter serial number"
                        className="input-admin text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
                      />
                    </div>
                    
                    {/* Issue Date */}
                    <div className="form-group">
                      <label className="form-label">
                        Call-up Issue Date
                      </label>
                      <DatePickerField
                        selected={formData.issue_date ? parseLocalISOToDate(formData.issue_date) : null}
                        onChange={(date) => setFormData(prev => ({ ...prev, issue_date: date ? toLocalISODate(date) : '' }))}
                        className="input-admin text-sm pl-10 pr-8 w-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
                        placeholderText="Select issue date"
                        portalId="datepicker-portal"
                        popperClassName="datepicker-popper-high-z"
                        popperPlacement="bottom-start"
                      />
                    </div>
                    
                    {/* Activity Type */}
                    <div className="form-group">
                      <label className="form-label">
                        NS Activity Type
                      </label>
                      <FormSelect
                        value={formData.activity_type}
                        onChange={(val) => setFormData(prev => ({ ...prev, activity_type: val }))}
                        options={[
                          { value: '', label: 'Select activity type...' },
                          ...nsActivityTypes.map(type => ({ value: type, label: type }))
                        ]}
                        theme="employee"
                      />
                    </div>
                    
                    {/* Start Date + AM/PM */}
                    <div className="form-group">
                      <label className="form-label">
                        Start Date
                      </label>
                      <div className="flex gap-2">
                        <div className="flex-1">
                          <DatePickerField
                            selected={formData.leave_start_date ? parseLocalISOToDate(formData.leave_start_date) : null}
                            onChange={(date) => setFormData(prev => ({ ...prev, leave_start_date: date ? toLocalISODate(date) : '' }))}
                            className="input-admin text-sm pl-10 pr-8 w-full focus:outline-none focus:ring-2 focus:ring-red-500"
                            placeholderText="Select start date"
                            portalId="datepicker-portal"
                            popperClassName="datepicker-popper-high-z"
                            popperPlacement="bottom-start"
                          />
                        </div>
                        <LeaveHalfDaySegmentedControl
                          theme="admin"
                          value={formData.leave_start_time_period}
                          onChange={(period) => setFormData(prev => ({ ...prev, leave_start_time_period: period }))}
                          ariaLabel="Start day: morning or afternoon"
                        />
                      </div>
                    </div>
                    
                    {/* End Date + AM/PM */}
                    <div className="form-group">
                      <label className="form-label">
                        End Date
                      </label>
                      <div className="flex gap-2">
                        <div className="flex-1">
                          <DatePickerField
                            selected={formData.leave_end_date ? parseLocalISOToDate(formData.leave_end_date) : null}
                            onChange={(date) => setFormData(prev => ({ ...prev, leave_end_date: date ? toLocalISODate(date) : '' }))}
                            className="input-admin text-sm pl-10 pr-8 w-full focus:outline-none focus:ring-2 focus:ring-red-500"
                            placeholderText="Select end date"
                            minDate={formData.leave_start_date ? parseLocalISOToDate(formData.leave_start_date) : null}
                            portalId="datepicker-portal"
                            popperClassName="datepicker-popper-high-z"
                            popperPlacement="bottom-start"
                          />
                        </div>
                        <LeaveHalfDaySegmentedControl
                          theme="admin"
                          value={formData.leave_end_time_period}
                          onChange={(period) => setFormData(prev => ({ ...prev, leave_end_time_period: period }))}
                          ariaLabel="End day: morning or afternoon"
                        />
                      </div>
                    </div>
                    
                    {/* Notes */}
                    <div className="form-group">
                      <label className="form-label">
                        Verification Notes (Optional)
                      </label>
                      <input
                        type="text"
                        value={formData.notes}
                        onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                        placeholder="Add any verification notes..."
                        maxLength={500}
                        className="input-admin text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 w-full"
                      />
                    </div>
                  </div>

                  {/* Rejection Reason Inline Form */}
                  {rejectionMode && (
                    <div className="px-4 py-3 bg-red-50 border-t border-red-200">
                      <label className="block text-sm font-medium text-red-800 mb-1">
                        Reason for Rejection <span className="text-red-600">*</span>
                      </label>
                      <textarea
                        value={rejectionReason}
                        onChange={(e) => setRejectionReason(e.target.value)}
                        placeholder="Provide a clear reason for rejecting this NS Call-up Order..."
                        className="textarea-admin text-sm input-error"
                        rows={2}
                        autoFocus={!isMobile}
                      />
                    </div>
                  )}

                  {/* Sticky Footer with Action Buttons — inside right panel */}
                  {(() => {
                    const docStatus = (verificationData?.document_approval?.status || '').toLowerCase();
                    const isPending = ['submitted', 'pending', 'ocr_completed'].includes(docStatus);
                    const isApproved = docStatus === 'approved' || docStatus === 'verified';
                    const isRejected = docStatus === 'rejected';
                    return (
                      <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4 flex-shrink-0">
                        {isPending ? (
                          rejectionMode ? (
                            <div className="flex gap-3 btn-row-spread">
                              <button
                                onClick={() => { setRejectionMode(false); setRejectionReason(''); }}
                                className="btn btn-admin-outline"
                                disabled={submitting}
                              >
                                Back
                              </button>
                              <button
                                onClick={handleRejectSubmit}
                                className="btn btn-admin-secondary"
                                disabled={submitting || !rejectionReason.trim()}
                              >
                                {submitting ? (
                                  <>
                                    <Loader className="w-4 h-4 mr-2 animate-spin" />
                                    Rejecting...
                                  </>
                                ) : (
                                  <>
                                    <XCircle className="w-4 h-4 mr-2" />
                                    Confirm Reject
                                  </>
                                )}
                              </button>
                            </div>
                          ) : (
                            <div className="flex gap-3 btn-row-spread">
                              <button
                                onClick={() => setRejectionMode(true)}
                                className="btn btn-admin-secondary"
                                disabled={submitting || loading}
                              >
                                <XCircle className="w-4 h-4 mr-2" />
                                Reject
                              </button>
                              <button
                                onClick={handleApprove}
                                className="btn btn-admin-primary"
                                disabled={submitting || loading}
                              >
                                {submitting ? (
                                  <>
                                    <Loader className="w-4 h-4 mr-2 animate-spin" />
                                    Processing...
                                  </>
                                ) : (
                                  <>
                                    <CheckCircle className="w-4 h-4 mr-2" />
                                    Approve
                                  </>
                                )}
                              </button>
                            </div>
                          )
                        ) : (
                          <div className="flex flex-col items-center gap-3">
                            {isApproved && (
                              <div className="flex items-center text-green-600">
                                <CheckCircle className="w-4 h-4 mr-1.5" />
                                <span className="text-sm font-medium">NS Call-up Verified &amp; Leave Approved</span>
                              </div>
                            )}
                            {isRejected && (
                              <div className="flex items-center text-red-600">
                                <XCircle className="w-4 h-4 mr-1.5" />
                                <span className="text-sm font-medium">NS Call-up Rejected</span>
                              </div>
                            )}
                            <button onClick={onClose} className="btn btn-admin-outline w-full">
                              Close
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </Portal>

    <ConfirmModal
      isOpen={showCloseConfirm}
      title="Close Without Saving?"
      message="The document has not been verified or rejected. Any field edits will be lost."
      confirmLabel="Close Anyway"
      cancelLabel="Go Back"
      variant="danger"
      theme="admin"
      onConfirm={() => { setShowCloseConfirm(false); onClose(); }}
      onCancel={() => setShowCloseConfirm(false)}
    />
    </>
  );
};

export default NSDocumentVerificationModal;
